import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Scrolling Padel-Pro news banner. Pulls latest FIP news (already FR-translated
 * server-side) and animates them seamlessly. Works on mobile too thanks to
 * `inline-flex` + `width: max-content` (no flex wrapping that would break the
 * animation).
 */
const NewsTicker = () => {
  const [items, setItems] = useState([]);
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'fr').slice(0, 2);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/padpro/news`, { params: { lang } })
      .then(r => { if (alive) setItems((r.data.data || []).slice(0, 10)); })
      .catch(() => { /* silently fail */ });
    return () => { alive = false; };
  }, [lang]);

  if (!items.length) return null;
  // Duplicate items so the marquee loops seamlessly (translate -50%).
  const loop = [...items, ...items];

  return (
    <div
      className="bg-gradient-to-r from-[#021E47] via-[#033871] to-[#021E47] border-y border-[#0C63BF]/40 relative w-full"
      data-testid="news-ticker"
    >
      <div className="flex items-stretch w-full">
        {/* Label */}
        <Link
          to="/padpro"
          className="shrink-0 flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-[#32CD32] hover:bg-[#28a428] text-white font-bold text-[10px] sm:text-xs uppercase tracking-wider transition-colors"
        >
          <Trophy className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          <span className="hidden sm:inline">Padel</span> Pro
        </Link>

        {/* Marquee — overflow hidden, inner uses inline-flex + width:max-content
            so the animation runs the same way on desktop and mobile. */}
        <div className="flex-1 overflow-hidden relative">
          {/* Side fades */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-[#021E47] to-transparent z-10" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-[#021E47] to-transparent z-10" />
          <div className="ticker-track">
            {loop.map((n, i) => (
              <Link
                key={n.id + '-' + i}
                to="/padpro"
                className="inline-flex items-center gap-2 px-3 sm:px-5 py-2 text-white/85 hover:text-white text-[11px] sm:text-sm transition-colors group"
                data-testid={`ticker-item-${n.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#32CD32] shrink-0" />
                <span className="font-medium">{n.title}</span>
                <ChevronRight className="w-3 h-3 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all hidden sm:inline-block" />
              </Link>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ticker-marquee {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        .ticker-track {
          display: inline-flex;
          flex-wrap: nowrap;
          width: max-content;
          will-change: transform;
          animation: ticker-marquee 60s linear infinite;
        }
        .ticker-track:hover { animation-play-state: paused; }
        @media (max-width: 640px) {
          .ticker-track { animation-duration: 38s; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ticker-track { animation-duration: 120s; }
        }
      `}</style>
    </div>
  );
};

export default NewsTicker;
